import { v4 as uuidv4 } from "uuid";
import { create } from "zustand";
// import { persist } from "zustand/middleware"; // Remove persist
import { dbHelpers } from "../lib/db";
import { Playlist, Track } from "../types";

interface PlaylistState {
  playlists: Playlist[];
  initialize: () => Promise<void>;
  createPlaylist: (
    name: string,
    description?: string,
    coverUrl?: string,
    tags?: string[],
    source?: "spotify" | "local"
  ) => string;
  deletePlaylist: (id: string) => void;
  addTrackToPlaylist: (playlistId: string, track: Track) => void;
  removeTrackFromPlaylist: (playlistId: string, trackId: string) => void;
  updatePlaylist: (id: string, updates: Partial<Playlist>) => void;
}

export const usePlaylistStore = create<PlaylistState>((set, get) => ({
  playlists: [],

  initialize: async () => {
    if (get().playlists.length > 0) return;

    try {
      const storedPlaylists = await dbHelpers.getAllPlaylists();
      set({ playlists: storedPlaylists });
    } catch (error) {
      console.error("Error initializing playlists:", error);
    }
  },

  createPlaylist: (name, description, coverUrl, tags = [], source = "local") => {
    const id = uuidv4();
    const now = Date.now();
    const newPlaylist: Playlist = {
      id,
      name,
      description,
      coverUrl,
      tracks: [],
      createdAt: now,
      updatedAt: now,
      tags,
      source,
      isAlbum: false, // Ensure isAlbum is false for playlists
    };
    set((state) => ({
      playlists: [...state.playlists, newPlaylist],
    }));
    // Persist to IndexedDB
    dbHelpers.addPlaylist(newPlaylist).catch(console.error);
    return id;
  },

  deletePlaylist: (id) => {
    set((state) => ({
      playlists: state.playlists.filter((p) => p.id !== id),
    }));
    // Persist to IndexedDB
    dbHelpers.deletePlaylist(id).catch(console.error);
  },

  addTrackToPlaylist: (playlistId, track) => {
    set((state) => ({
      playlists: state.playlists.map((p) =>
        p.id === playlistId
          ? {
              ...p,
              tracks: [...p.tracks, track],
              updatedAt: Date.now(),
            }
          : p
      ),
    }));
    // Persist to IndexedDB
    dbHelpers.addPlaylist(get().playlists.find((p) => p.id === playlistId) as Playlist).catch(console.error);
  },

  removeTrackFromPlaylist: (playlistId, trackId) => {
    set((state) => ({
      playlists: state.playlists.map((p) =>
        p.id === playlistId
          ? {
              ...p,
              tracks: p.tracks.filter((t) => t.id !== trackId),
              updatedAt: Date.now(),
            }
          : p
      ),
    }));
    // Persist to IndexedDB
    dbHelpers.addPlaylist(get().playlists.find((p) => p.id === playlistId) as Playlist).catch(console.error);
  },

  updatePlaylist: (id, updates) => {
    set((state) => ({
      playlists: state.playlists.map((p) =>
        p.id === id
          ? {
              ...p,
              ...updates,
              updatedAt: Date.now(),
            }
          : p
      ),
    }));
    // Persist to IndexedDB
    dbHelpers.addPlaylist(get().playlists.find((p) => p.id === id) as Playlist).catch(console.error);
  },
}));

// Initialize the store
usePlaylistStore.getState().initialize();

// Subscribe to changes and persist to IndexedDB
usePlaylistStore.subscribe((state) => {
  state.playlists.forEach((playlist) => {
    dbHelpers.addPlaylist(playlist).catch(console.error);
  });
});
